import { useCallback, useContext, useState } from 'react';

import { DepositContext } from '~/context/Deposit/DepositContext';
import { useUpdateAllocationClassification } from '~/hooks/managed/useUpdateAllocationClassification';
import { useWhiskeyPortfolioQuery } from '~/hooks/queries/portfolios/whiskey/useWhiskeyPortfolioQuery';

import { ConnectCreditCard } from './ConnectCreditCard';
import { CryptocurrencyTransfer } from './CryptocurrencyTransfer';
import { DomesticWireTransferInstructions } from './DomesticWireTransferInstructions';
import { InternationalWireTransferInstructions } from './InternationalWireTransferInstructions';
import { SelectSource } from './SelectSource';

interface ConnectSourceModalContentProps {
    setIsPlaidComplete: (completed: boolean) => void;
    onPaymentAdded: () => void;
}

/**
 * Component used to link user payment methods with their vinovest account.
 */
export const ConnectSourceModalContent = ({ setIsPlaidComplete, onPaymentAdded }: ConnectSourceModalContentProps) => {
    const [step, setStep] = useState(0);
    const { allocationClassification } = useContext(DepositContext);

    const { mutate } = useUpdateAllocationClassification();
    const { data: whiskeyPortfolio } = useWhiskeyPortfolioQuery();

    let component;
    let title = 'Connect Account';

    const onPaymentLinked = () => {
        onPaymentAdded();
    };

    const updateAllocationClassification = useCallback(() => {
        if (allocationClassification != null && whiskeyPortfolio?.id != null) {
            mutate({ payload: { allocationClassification }, portfolioID: whiskeyPortfolio.id });
        }
    }, [allocationClassification, whiskeyPortfolio]);

    switch (step) {
        case 0:
            component = (
                <SelectSource
                    setStep={setStep}
                    onPaymentLinked={onPaymentLinked}
                    setIsPlaidComplete={setIsPlaidComplete}
                />
            );
            title = 'Connect Account';
            break;
        case 2:
            component = <ConnectCreditCard onPaymentLinked={onPaymentLinked} setStep={setStep} />;
            title = 'Credit Card';
            break;
        case 3:
            component = <CryptocurrencyTransfer setStep={setStep} onPaymentLinked={onPaymentLinked} />;
            title = 'Crypto Currency';
            break;
        case 4:
            component = <DomesticWireTransferInstructions setStep={setStep} onView={updateAllocationClassification} />;
            title = 'US Wire Transfer';
            break;
        case 5:
            component = (
                <InternationalWireTransferInstructions setStep={setStep} onView={updateAllocationClassification} />
            );
            title = 'International Wire Transfer';
            break;
        default:
            component = (
                <SelectSource
                    onPaymentLinked={onPaymentLinked}
                    setStep={setStep}
                    setIsPlaidComplete={setIsPlaidComplete}
                />
            );
            title = 'Connect Account';
    }

    const modalWidth = step === 1 ? 'md:max-w-[817px]' : 'md:max-w-[400px]';

    return (
        <div className={` w-full print:w-full ${modalWidth}`}>
            <h2 className={'text-20 font-roslindale -mt-4 lg:mt-0 mb-6'}>{title}</h2>
            {component}
        </div>
    );
};
